import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Typography,
  Box,
  FormHelperText,
  Divider,
} from "@mui/material";
import * as mapboxStyle from "mui-components/kart/mapcomponent/MapboxStyle";
import { FormattedMessage } from "react-intl";
import CheckIcon from "@mui/icons-material/Check";

const MuiSelectMobileDropdown = (props) => {
  const {
    defaultLabel,
    defaultValue,
    options,
    fieldName,
    onChange,
    textLabel,
    required,
    errorMessage,
    deSelect = true,
    borderLine = false,
    defaultOptionsLength = 0,
    bottomSheetHeight = "50vh",
    disabled = false, // 👈 Add the disabled prop here
    activityId = null
  } = props;

  const [value, setValue] = useState(defaultValue || "");
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleOpenSelect = () => {
    if (!disabled) { // 👈 Only open if not disabled
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (required && !value) {
      setTouched(true);
    }
  };

  const handleChange = (selectedValue) => {
    if (!disabled) { // 👈 Only allow change if not disabled
      let newValue = "";

      if (deSelect) {
        newValue = selectedValue === value ? "" : selectedValue;
      } else {
        newValue = selectedValue;
      }

      setValue(newValue);
      setTouched(true);

      if (onChange) {
        onChange(fieldName, newValue);
      }

      setOpen(false);
    }
  };

  const shouldShowError = required && touched && !value;

  return (
    <div style={{ width: "100%" }}>
      <FormControl
        fullWidth
        variant="outlined"
        onClick={handleOpenSelect}
        error={shouldShowError}
        disabled={disabled} // 👈 Apply the disabled prop to FormControl
        sx={{
          "& label": {
            color: disabled ? "#B0B0B0" : "#3F4949", // Adjust label color when disabled
            fontFamily: "Inter",
          },
          "& label.Mui-focused": {
            color: "#00696D",
            fontFamily: "Inter",
          },
          "& .MuiOutlinedInput-root": {
            ...(shouldShowError && {
              borderColor: "#d32f2f !important",
            }),
            ...(disabled && { // Style when disabled
              backgroundColor: '#e1e1e1',
              cursor: 'not-allowed',
            }),
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: disabled ? "#B0B0B0" : undefined, // Adjust border color when disabled
          },
        }}
      >
        <InputLabel>{defaultLabel}</InputLabel>
        <Select
          value={value}
          readOnly
          label={defaultLabel}
          IconComponent={() => null}
          disabled={disabled} // 👈 Apply the disabled prop to Select
          sx={{
            '& .MuiSelect-select.Mui-disabled': {
              color: '#B0B0B0', // Adjust text color when disabled
            },
          }}
        >
          <MenuItem value={value}>
            {options?.find((opt) => opt.value === value)?.label || ""}
          </MenuItem>
        </Select>
        {shouldShowError ? (
          <FormHelperText sx={{
            '&.MuiFormHelperText-root': activityId === 'act19' ? {
              height: '24px !important',
              margin: '0px !important',
            } : {}
          }}>
            {errorMessage || "Dette feltet er påkrevd"}
          </FormHelperText>
        ) : (
          <FormHelperText sx={{
            '&.MuiFormHelperText-root': activityId === 'act19' ? {
              height: '24px !important',
              margin: '0px !important',
            } : {}
          }}>&nbsp;</FormHelperText>
        )}
      </FormControl>

      <Dialog
        fullWidth
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            position: "absolute",
            bottom: 0,
            margin: 0,
            width: "100%",
            borderRadius: "28px 28px 0 0",
            padding: 0,
            maxHeight: bottomSheetHeight,
          },
        }}
      >
        <DialogContent
          sx={{
            padding: "0px 0px 20px 0px",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ padding: "35px 20px 16px 20px", textAlign: "left" }}>
            <Typography
              sx={{
                color: "#191C1C",
                fontFamily: "Inter",
                fontSize: "22px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "28px",
              }}
            >
              {textLabel}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto" }}>
            <List>
              {options?.map((option, index) => {
                const isSelected = value === option.value;

                const listItem = (
                  <ListItem disablePadding key={option.value}>
                    <ListItemButton
                      onClick={() => handleChange(option.value)}
                      sx={{
                        backgroundColor: isSelected ? "#D8DADB" : "",
                        "&:hover": {
                          backgroundColor: "#D8DADB",
                        },
                        padding: "12px 24px 12px 18px",
                      }}
                      disabled={disabled} // You might not need to disable the list items themselves, but consider it
                    >
                      <Typography
                        sx={{
                          flexGrow: 1,
                          textAlign: "left",
                          color: disabled ? "#B0B0B0" : "#191C1C", // Adjust text color when disabled
                        }}
                      >
                        {option.label}
                      </Typography>
                      {isSelected && (
                        <ListItemIcon
                          sx={{ minWidth: "auto", color: "#3F4949" }}
                        >
                          <CheckIcon />
                        </ListItemIcon>
                      )}
                    </ListItemButton>
                  </ListItem>
                );

                if (borderLine) {
                  return (
                    <Box key={option.value}>
                      {index === 0 && defaultOptionsLength > 0 && (
                        <Box
                          sx={{
                            padding: "10px 20px",
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Typography
                            style={mapboxStyle.modalTitleSubHeader}
                            sx={{ color: disabled ? "#B0B0B0" : undefined }}
                          >
                            <FormattedMessage id="USED_IN_PLAN" />
                          </Typography>
                        </Box>
                      )}
                      {listItem}
                      {defaultOptionsLength > 0 &&
                        defaultOptionsLength === index + 1 && (
                          <Divider
                            sx={{
                              mx: 2,
                              borderColor: "#BEC8C9",
                              borderBottomWidth: 1,
                            }}
                          />
                        )}
                    </Box>
                  );
                }

                return listItem;
              })}
            </List>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MuiSelectMobileDropdown;
